import { supabase } from '../supabase';
import { PlatformId } from '../store/usePostingStore';
import { SocialMediaPoster, PostPayload, PostResponse } from './SocialMediaPoster';

/**
 * Post Processor Service
 * Handles processing and posting content to multiple platforms
 */
export class PostProcessor {
  private poster: SocialMediaPoster;
  private userId: string;

  constructor(poster: SocialMediaPoster, userId: string) {
    this.poster = poster;
    this.userId = userId;
  }

  /**
   * Process and post content to multiple platforms
   */
  public async processMultiPlatformPost(payloads: Record<PlatformId, PostPayload>): Promise<Record<PlatformId, PostResponse>> {
    const results: Record<PlatformId, PostResponse> = {} as Record<PlatformId, PostResponse>;
    
    // Create a record of the multi-platform post
    const { data: multiPost, error: multiPostError } = await supabase
      .from('multi_platform_posts')
      .insert({
        user_id: this.userId,
        platforms: Object.keys(payloads),
        status: 'processing',
        created_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (multiPostError) {
      console.error('Error creating multi-platform post record:', multiPostError);
    }
    
    // Process each platform payload
    for (const platformId in payloads) {
      try {
        console.log(`Processing post for ${platformId}...`);
        
        // Process media files first
        const processedPayload = await this.processMediaFiles(payloads[platformId as PlatformId]);
        
        // Get the social account for this platform
        const { data: socialAccount, error: accountError } = await supabase
          .from('social_accounts')
          .select('*')
          .eq('user_id', this.userId)
          .eq('platform', platformId)
          .eq('connected', true)
          .single();
        
        if (accountError || !socialAccount) {
          throw new Error(`No connected ${platformId} account found`);
        }
        
        // Ensure we have the right scopes for posting
        if (platformId === 'x' && (!socialAccount.scope || !socialAccount.scope.includes('tweet.write'))) {
          throw new Error(`Your ${platformId} account doesn't have write permissions. Please reconnect with the proper scopes.`);
        }
        
        // Use the platform-specific poster method
        let result: PostResponse;
        if (platformId === 'x') {
          result = await this.poster.postToTwitter({
            ...processedPayload,
            auth: {
              accessToken: socialAccount.access_token,
              tokenType: 'Bearer',
              expiresIn: socialAccount.expires_in || 0,
              refreshToken: socialAccount.refresh_token
            }
          });
        } else if (platformId === 'fb') {
          result = await this.poster.postToFacebook({
            ...processedPayload,
            auth: {
              accessToken: socialAccount.access_token,
              tokenType: 'Bearer',
              expiresIn: socialAccount.expires_in || 0,
              refreshToken: socialAccount.refresh_token
            }
          });
        } else if (platformId === 'ig') {
          result = await this.poster.postToInstagram({
            ...processedPayload,
            auth: {
              accessToken: socialAccount.access_token,
              tokenType: 'Bearer',
              expiresIn: socialAccount.expires_in || 0,
              refreshToken: socialAccount.refresh_token
            }
          });
        } else if (platformId === 'ln') {
          result = await this.poster.postToLinkedIn({
            ...processedPayload,
            auth: {
              accessToken: socialAccount.access_token,
              tokenType: 'Bearer',
              expiresIn: socialAccount.expires_in || 0,
              refreshToken: socialAccount.refresh_token
            }
          });
        } else if (platformId === 'yt') {
          result = await this.poster.postToYouTube({
            ...processedPayload,
            auth: {
              accessToken: socialAccount.access_token,
              tokenType: 'Bearer',
              expiresIn: socialAccount.expires_in || 0,
              refreshToken: socialAccount.refresh_token
            }
          });
        } else if (platformId === 'tt') {
          // TikTok implementation will be added in the future
          result = {
            success: false,
            platformId: platformId as PlatformId,
            message: 'TikTok posting is not yet implemented'
          };
        } else {
          // For other platforms, use the generic post method
          result = await this.poster.post({
            ...processedPayload,
            auth: {
              accessToken: socialAccount.access_token,
              tokenType: 'Bearer',
              expiresIn: socialAccount.expires_in || 0,
              refreshToken: socialAccount.refresh_token
            }
          });
        }
        
        // Store the result
        results[platformId as PlatformId] = result;
        
        // Log the result
        if (result.success) {
          console.log(`Successfully posted to ${platformId}: ${result.message || 'Posted successfully'}`);
        } else {
          console.error(`Failed to post to ${platformId}: ${result.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`Error processing post for ${platformId}:`, error);
        
        // Store the error
        results[platformId as PlatformId] = {
          success: false,
          platformId: platformId as PlatformId,
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          error
        };
      }
    }
    
    return results;
  }

  /**
   * Process media files in the payload
   * Converts blob URLs to base64 data
   */
  private async processMediaFiles(payload: PostPayload): Promise<PostPayload> {
    const processedPayload = { ...payload };
    const processedMedia = [];
    
    // Process each media file
    for (const media of payload.content.media) {
      try {
        // Check if the URL is a blob URL
        if (media.url.startsWith('blob:')) {
          // Fetch the blob
          const response = await fetch(media.url);
          const blob = await response.blob();
          
          // Convert blob to base64
          const reader = new FileReader();
          const base64Promise = new Promise<string>((resolve) => {
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          
          const base64Data = await base64Promise;
          
          // Add to processed media with base64 data
          processedMedia.push({
            ...media,
            data: base64Data // Add base64 data for the server to process
          });
        } else {
          // URL is already a public URL, use as is
          processedMedia.push(media);
        }
      } catch (error) {
        console.error('Error processing media:', error);
        throw new Error(`Failed to process media file ${media.name}`);
      }
    }
    
    // Update the payload with processed media
    processedPayload.content.media = processedMedia;
    
    // Process thumbnail if it exists
    if (payload.content.thumbnail && payload.content.thumbnail.url.startsWith('blob:')) {
      try {
        const thumbnail = payload.content.thumbnail;
        
        // Fetch the blob
        const response = await fetch(thumbnail.url);
        const blob = await response.blob();
        
        // Convert blob to base64
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
        
        const base64Data = await base64Promise;
        
        // Update the thumbnail with base64 data
        processedPayload.content.thumbnail = {
          ...thumbnail,
          data: base64Data
        };
      } catch (error) {
        console.error('Error processing thumbnail:', error);
        throw new Error(`Failed to process thumbnail`);
      }
    }
    
    return processedPayload;
  }

  /**
   * Save post results to the database
   */
  public async savePostResults(results: Record<PlatformId, PostResponse>): Promise<void> {
    try {
      // Create a record of the multi-platform post
      const { data: multiPost, error: multiPostError } = await supabase
        .from('multi_platform_posts')
        .insert({
          user_id: this.userId,
          platforms: Object.keys(results),
          status: Object.values(results).every(r => r.success) ? 'success' : 'partial',
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (multiPostError) throw multiPostError;
      
      // Save individual platform results
      for (const platformId in results) {
        const result = results[platformId as PlatformId];
        
        await supabase
          .from('platform_post_results')
          .insert({
            multi_post_id: multiPost.id,
            platform: platformId,
            success: result.success,
            message: result.message,
            post_id: result.postId,
            url: result.url,
            error: result.error ? JSON.stringify(result.error) : null
          });
      }
    } catch (error) {
      console.error('Error saving post results:', error);
    }
  }
}

/**
 * Create a new instance of the PostProcessor
 */
export const createPostProcessor = async (): Promise<PostProcessor | null> => {
  try {
    // Get current user
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    if (!user) return null;
    
    // Import dynamically to avoid circular dependencies
    const { createSocialMediaPoster } = await import('./SocialMediaPoster');
    
    // Create a social media poster
    const poster = await createSocialMediaPoster();
    if (!poster) return null;
    
    return new PostProcessor(poster, user.id);
  } catch (error) {
    console.error('Error creating post processor:', error);
    return null;
  }
};